import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { sortObjectOptions } from '@/utils/sortOptions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Plus, Search, Edit, Trash2, Eye, X, Languages, Layers } from 'lucide-react';
import axios from 'axios';

// Backend URL fallback to local dev if env not set
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
const API = `${BACKEND_URL}/api`;

// Available languages (currently English and Hindi only)
const AVAILABLE_LANGUAGES = ['English', 'Hindi'];

const QuestionBankPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [questionCount, setQuestionCount] = useState(0);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [filters, setFilters] = useState({
    topic_id: 'all',
    section_id: 'all',
    search: '',
    purpose: 'all'
  });

  // Language tabs management
  const [activeLangTabs, setActiveLangTabs] = useState(['English']);
  const [currentTab, setCurrentTab] = useState('English');
  const [showAddLangDialog, setShowAddLangDialog] = useState(false);

  // Form data - common fields (shared across languages)
  const [formData, setFormData] = useState({
    question_type: 'mcq_single',
    purpose: 'conventional',
    difficulty: 'medium',
    tags: [],
    points: 1,
    topic_id: undefined,
    section_id: undefined
  });

  // Adaptive training - simple sequence dropdown
  const [availableSequences, setAvailableSequences] = useState([1]);
  const [selectedSequence, setSelectedSequence] = useState(1);

  // Per-language content storage
  const [languageContent, setLanguageContent] = useState({
    'English': {
      question_text: '',
      options: [
        { option_id: 'opt-1', text: '', is_correct: false },
        { option_id: 'opt-2', text: '', is_correct: false }
      ],
      right_instruction: '',
      wrong_instruction: ''
    }
  });

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchQuestions();
    fetchTrainingTopics();
  }, [filters]);

  const fetchTrainingTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/training/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingTopics(response.data || []);
    } catch (error) {
      console.error('Failed to fetch training chapters:', error);
    }
  };

  const getChapterId = (chapter) => chapter?.id || chapter?.training_topic_id || chapter?.content_id;

  const getSubchapterId = (subchapter, index = 0) => (
    subchapter?.id ||
    subchapter?.section_id ||
    subchapter?.section_title ||
    subchapter?.title ||
    `subchapter-${index}`
  );

  const getSubchapterTitle = (subchapter, index = 0) => (
    subchapter?.section_title || subchapter?.title || `Subchapter ${index + 1}`
  );

  const findChapterById = (chapterId, source = trainingTopics) => {
    if (!chapterId) return null;
    const chapterIdStr = String(chapterId);
    return source.find(chapter => String(getChapterId(chapter)) === chapterIdStr) || null;
  };

  const findSubchapterById = (chapter, subchapterId) => {
    if (!chapter || !subchapterId) return null;
    const subchapterIdStr = String(subchapterId);
    return (chapter.sections || []).find((section, index) => {
      const possibleIds = [
        getSubchapterId(section, index),
        section?.id,
        section?.section_id,
        section?.section_title,
        section?.title
      ].filter(Boolean).map(String);
      return possibleIds.includes(subchapterIdStr);
    }) || null;
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to access Question Bank');
        navigate('/signin');
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      if (filters.topic_id && filters.topic_id !== 'all') params.append('topic_id', filters.topic_id);
      if (filters.section_id && filters.section_id !== 'all') params.append('section_id', filters.section_id);
      if (filters.search) params.append('search', filters.search);
      if (filters.purpose && filters.purpose !== 'all') params.append('purpose', filters.purpose);

      const response = await axios.get(`${API}/assessments/questions?${params}` , {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
      setQuestionCount(response.data.count || 0);
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to fetch questions';
      toast.error(msg);
    }
    setLoading(false);
  };

  const initLanguageContent = (lang) => {
    const englishContent = languageContent['English'] || {};
    const englishOptions = englishContent.options || [];
    
    return {
      question_text: '',
      options: englishOptions.map(opt => ({
        option_id: opt.option_id,
        text: '',
        is_correct: opt.is_correct
      })),
      right_instruction: '',
      wrong_instruction: ''
    };
  };

  const handleAddLanguage = (lang) => {
    if (!activeLangTabs.includes(lang)) {
      setActiveLangTabs([...activeLangTabs, lang]);
      setLanguageContent({
        ...languageContent,
        [lang]: initLanguageContent(lang)
      });
      setCurrentTab(lang);
    }
    setShowAddLangDialog(false);
  };

  const handleRemoveLanguage = (lang) => {
    if (lang === 'English') {
      toast.error('English is required and cannot be removed');
      return;
    }
    const updatedTabs = activeLangTabs.filter(l => l !== lang);
    const updatedContent = { ...languageContent };
    delete updatedContent[lang];
    
    setActiveLangTabs(updatedTabs);
    setLanguageContent(updatedContent);
    setCurrentTab('English');
  };

  const updateCurrentLangContent = (field, value) => {
    setLanguageContent({
      ...languageContent,
      [currentTab]: {
        ...languageContent[currentTab],
        [field]: value
      }
    });
  };

  const getCurrentContent = () => {
    return languageContent[currentTab] || initLanguageContent(currentTab);
  };

  const addOption = () => {
    const newOptionId = `opt-${Date.now()}`;
    const newOption = {
      option_id: newOptionId,
      text: '',
      is_correct: false
    };
    
    const updatedContent = { ...languageContent };
    activeLangTabs.forEach(lang => {
      if (updatedContent[lang]) {
        updatedContent[lang] = {
          ...updatedContent[lang],
          options: [...(updatedContent[lang].options || []), { ...newOption }]
        };
      }
    });
    setLanguageContent(updatedContent);
  };

  const updateOption = (index, field, value) => {
    const currentContent = getCurrentContent();
    const updatedOptions = [...(currentContent.options || [])];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    
    if (field === 'is_correct') {
      if (value && formData.question_type === 'mcq_single') {
        updatedOptions.forEach((opt, i) => {
          if (i !== index) opt.is_correct = false;
        });
      }
      
      const updatedContent = { ...languageContent };
      activeLangTabs.forEach(lang => {
        if (updatedContent[lang] && updatedContent[lang].options) {
          updatedContent[lang].options = updatedContent[lang].options.map((opt, i) => ({
            ...opt,
            is_correct: updatedOptions[i]?.is_correct || false
          }));
        }
      });
      setLanguageContent(updatedContent);
    } else {
      updateCurrentLangContent('options', updatedOptions);
    }
  };

  const removeOption = (index) => {
    const updatedContent = { ...languageContent };
    activeLangTabs.forEach(lang => {
      if (updatedContent[lang] && updatedContent[lang].options) {
        updatedContent[lang].options = updatedContent[lang].options.filter((_, i) => i !== index);
      }
    });
    setLanguageContent(updatedContent);
  };

  const handleCreateQuestion = async () => {
    const englishContent = languageContent['English'];
    
    if (!formData.topic_id) {
      toast.error('Please select a Training Chapter');
      setCurrentTab('English');
      return;
    }

    // Adaptive mode validation
    if (formData.purpose === 'adaptive') {
      if (!englishContent?.question_text?.trim()) {
        toast.error('English question text is required');
        setCurrentTab('English');
        return;
      }
      if (!englishContent?.options || englishContent.options.length < 2) {
        toast.error('At least 2 options are required');
        setCurrentTab('English');
        return;
      }
      const hasValidOptions = englishContent.options.some(opt => opt.text?.trim());
      if (!hasValidOptions) {
        toast.error('Please enter option text for English');
        setCurrentTab('English');
        return;
      }
      const hasCorrectAnswer = englishContent.options.some(opt => opt.is_correct);
      if (!hasCorrectAnswer && ['mcq_single', 'mcq_multiple'].includes(formData.question_type)) {
        toast.error('Please mark at least one correct answer');
        return;
      }

      try {
        const token = localStorage.getItem('token');

        const questionTextTranslations = {};
        const rightInstructionTranslations = {};
        const wrongInstructionTranslations = {};
        const englishOptions = englishContent.options || [];
        const optionsWithTranslations = englishOptions.map((engOpt, idx) => {
          const textTranslations = {};
          activeLangTabs.forEach(lang => {
            const langOpt = languageContent[lang]?.options?.[idx];
            if (langOpt?.text) textTranslations[lang] = langOpt.text;
          });
          return {
            option_id: engOpt.option_id,
            text: engOpt.text,
            text_translations: textTranslations,
            is_correct: engOpt.is_correct
          };
        });
        activeLangTabs.forEach(lang => {
          const content = languageContent[lang];
          if (content) {
            if (content.question_text) questionTextTranslations[lang] = content.question_text;
            if (content.right_instruction) rightInstructionTranslations[lang] = content.right_instruction;
            if (content.wrong_instruction) wrongInstructionTranslations[lang] = content.wrong_instruction;
          }
        });

        const payload = {
          question_type: formData.question_type,
          purpose: 'adaptive',
          sequence_number: selectedSequence,
          question_text: englishContent.question_text,
          question_text_translations: questionTextTranslations,
          options: optionsWithTranslations,
          difficulty: formData.difficulty,
          tags: formData.tags,
          points: formData.points,
          score: formData.points,
          topic_id: formData.topic_id,
          section_id: formData.section_id || null,
          right_instruction: englishContent.right_instruction || '',
          right_instruction_translations: Object.keys(rightInstructionTranslations).length > 0 ? rightInstructionTranslations : null,
          wrong_instruction: englishContent.wrong_instruction || '',
          wrong_instruction_translations: Object.keys(wrongInstructionTranslations).length > 0 ? wrongInstructionTranslations : null,
          available_languages: activeLangTabs,
          language: 'English'
        };

        if (['ranking', 'arrange'].includes(formData.question_type)) {
          payload.correct_order = optionsWithTranslations.map(opt => opt.option_id);
        }

        if (isEditMode && selectedQuestion) {
          await axios.put(`${API}/assessments/questions/${selectedQuestion.question_id}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Question updated successfully!');
        } else {
          await axios.post(`${API}/assessments/questions`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Question created successfully!');
        }

        setShowCreateModal(false);
        resetForm();
        fetchQuestions();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save question');
      }
      return;
    }

    // Conventional mode (existing logic)
    if (!englishContent?.question_text?.trim()) {
      toast.error('English question text is required');
      setCurrentTab('English');
      return;
    }
    
    if (!englishContent?.options || englishContent.options.length < 2) {
      toast.error('At least 2 options are required');
      setCurrentTab('English');
      return;
    }
    
    const hasValidOptions = englishContent.options.some(opt => opt.text?.trim());
    if (!hasValidOptions) {
      toast.error('Please enter option text for English');
      setCurrentTab('English');
      return;
    }
    
    const hasCorrectAnswer = englishContent.options.some(opt => opt.is_correct);
    if (!hasCorrectAnswer && ['mcq_single', 'mcq_multiple'].includes(formData.question_type)) {
      toast.error('Please mark at least one correct answer');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const questionTextTranslations = {};
      const rightInstructionTranslations = {};
      const wrongInstructionTranslations = {};
      
      const englishOptions = englishContent.options || [];
      const optionsWithTranslations = englishOptions.map((engOpt, idx) => {
        const textTranslations = {};
        activeLangTabs.forEach(lang => {
          const langOpt = languageContent[lang]?.options?.[idx];
          if (langOpt?.text) {
            textTranslations[lang] = langOpt.text;
          }
        });
        
        return {
          option_id: engOpt.option_id,
          text: engOpt.text,
          text_translations: textTranslations,
          is_correct: engOpt.is_correct
        };
      });
      
      activeLangTabs.forEach(lang => {
        const content = languageContent[lang];
        if (content) {
          if (content.question_text) questionTextTranslations[lang] = content.question_text;
          if (content.right_instruction) rightInstructionTranslations[lang] = content.right_instruction;
          if (content.wrong_instruction) wrongInstructionTranslations[lang] = content.wrong_instruction;
        }
      });

      const payload = {
        question_type: formData.question_type,
        purpose: formData.purpose,
        question_text: englishContent.question_text,
        question_text_translations: questionTextTranslations,
        options: optionsWithTranslations,
        difficulty: formData.difficulty,
        tags: formData.tags,
        points: formData.points,
        score: formData.points,
        topic_id: formData.topic_id,
        section_id: formData.section_id || null,
        right_instruction: englishContent.right_instruction || '',
        right_instruction_translations: Object.keys(rightInstructionTranslations).length > 0 ? rightInstructionTranslations : null,
        wrong_instruction: englishContent.wrong_instruction || '',
        wrong_instruction_translations: Object.keys(wrongInstructionTranslations).length > 0 ? wrongInstructionTranslations : null,
        available_languages: activeLangTabs,
        language: 'English'
      };

      if (['ranking', 'arrange'].includes(formData.question_type)) {
        payload.correct_order = optionsWithTranslations.map(opt => opt.option_id);
      }

      if (isEditMode && selectedQuestion) {
        await axios.put(`${API}/assessments/questions/${selectedQuestion.question_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question updated successfully!');
      } else {
        await axios.post(`${API}/assessments/questions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question created successfully!');
      }

      setShowCreateModal(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    }
  };

  const handleEditQuestion = (question) => {
    setSelectedQuestion(question);
    setIsEditMode(true);
    
    // Hospital Admin always uses conventional purpose
    const isHospitalAdmin = user?.role === 'Hospital Admin';
    const questionPurpose = isHospitalAdmin ? 'conventional' : (question.purpose || 'conventional');
    
    setFormData({
      question_type: question.question_type,
      purpose: questionPurpose,
      difficulty: question.difficulty || 'medium',
      tags: question.tags || [],
      points: question.points || 1,
      topic_id: question.topic_id || undefined,
      section_id: question.section_id || undefined
    });
    
    // Load adaptive sequence number if present (only for non-Hospital Admin)
    if (!isHospitalAdmin && question.purpose === 'adaptive' && question.sequence_number) {
      setSelectedSequence(question.sequence_number);
      // Ensure the sequence number is in the available list
      const maxSeq = question.sequence_number;
      setAvailableSequences(Array.from({ length: Math.max(maxSeq, 1) }, (_, i) => i + 1));
    } else {
      setSelectedSequence(1);
      setAvailableSequences([1]);
    }
    
    const availableLanguages = question.available_languages || ['English'];
    setActiveLangTabs(availableLanguages);
    
    const newLanguageContent = {};
    availableLanguages.forEach(lang => {
      const isEnglish = lang === 'English';
      
      const questionText = isEnglish 
        ? question.question_text 
        : (question.question_text_translations?.[lang] || '');
      
      const options = (question.options || []).map(opt => ({
        option_id: opt.option_id,
        text: isEnglish ? opt.text : (opt.text_translations?.[lang] || ''),
        is_correct: opt.is_correct
      }));
      
      const rightInstruction = isEnglish 
        ? (question.right_instruction || '') 
        : (question.right_instruction_translations?.[lang] || '');
      const wrongInstruction = isEnglish 
        ? (question.wrong_instruction || '') 
        : (question.wrong_instruction_translations?.[lang] || '');
      
      newLanguageContent[lang] = {
        question_text: questionText,
        options: options.length > 0 ? options : [
          { option_id: 'opt-1', text: '', is_correct: false },
          { option_id: 'opt-2', text: '', is_correct: false }
        ],
        right_instruction: rightInstruction,
        wrong_instruction: wrongInstruction
      };
    });
    
    setLanguageContent(newLanguageContent);
    setCurrentTab('English');
    setShowCreateModal(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assessments/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question deleted successfully');
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  const handleViewQuestion = (question) => {
    setSelectedQuestion(question);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      question_type: 'mcq_single',
      purpose: 'conventional',
      difficulty: 'medium',
      tags: [],
      points: 1,
      topic_id: undefined,
      section_id: undefined
    });
    setAvailableSequences([1]);
    setSelectedSequence(1);
    setLanguageContent({
      'English': {
        question_text: '',
        options: [
          { option_id: 'opt-1', text: '', is_correct: false },
          { option_id: 'opt-2', text: '', is_correct: false }
        ],
        right_instruction: '',
        wrong_instruction: ''
      }
    });
    setActiveLangTabs(['English']);
    setCurrentTab('English');
    setIsEditMode(false);
    setSelectedQuestion(null);
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      mcq_single: 'MCQ Single',
      mcq_multiple: 'MCQ Multiple',
      ranking: 'Ranking',
      arrange: 'Arrange',
      drag_drop: 'Drag-Drop',
      matching: 'Matching'
    };
    return labels[type] || type;
  };

  const canModifyQuestion = (question) => {
    if (user?.role !== 'Hospital Admin') return true;
    return question?.created_by_id === user?.id;
  };

  // === Adaptive Sequence Helpers ===
  const addSequence = () => {
    const nextNum = availableSequences.length + 1;
    setAvailableSequences([...availableSequences, nextNum]);
    setSelectedSequence(nextNum);
  };

  const getEligibleTopics = () => {
    if (!user) return trainingTopics;
    
    // Hospital Admin restrictions
    if (user.role === 'Hospital Admin') {
      return trainingTopics.filter(topic => {
        // Must be Online or Hybrid (not Offline) - case insensitive check
        const trainingType = topic.training_type?.toLowerCase();
        // Treat undefined/null as 'online' (backward compatibility)
        const isEligibleType = trainingType === 'online' || trainingType === 'hybrid' || !trainingType;
        
        return isEligibleType;
      });
    }
    
    // Super Admin, Exlva Admin, Exlva Admin Lite see all topics
    return trainingTopics;
  };

  const getTopicName = (topicId) => {
    const topic = findChapterById(topicId);
    return topic?.title || 'Unknown Chapter';
  };

  const getSectionName = (topicId, sectionId) => {
    if (!sectionId) return null;
    const topic = findChapterById(topicId);
    const section = findSubchapterById(topic, sectionId);
    return section ? getSubchapterTitle(section) : null;
  };

  // Truncate text helper for subchapter names
  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get sections for selected topic in filter
  const getFilterSections = () => {
    if (!filters.topic_id || filters.topic_id === 'all') return [];
    const selectedTopic = findChapterById(filters.topic_id, getEligibleTopics());
    return selectedTopic?.sections || [];
  };

  const currentContent = getCurrentContent();

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Question Bank</h1>
            <p className="text-gray-600 mt-1">Create and manage assessment questions with multilingual support</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Question
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className={`grid grid-cols-1 gap-4 ${['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'ExlvaAdminLite'].includes(user?.role) ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
            <div>
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search questions..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label>Chapter Name</Label>
              <Select 
                value={filters.topic_id} 
                onValueChange={(val) => setFilters({ ...filters, topic_id: val, section_id: 'all' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Chapters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Chapters</SelectItem>
                  {sortObjectOptions(getEligibleTopics(), 'title').map((topic) => {
                    const topicId = getChapterId(topic);
                    if (!topicId) return null;
                    return (
                      <SelectItem key={String(topicId)} value={String(topicId)}>
                        {topic.title}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subchapter Name</Label>
              <Select 
                value={filters.section_id} 
                onValueChange={(val) => setFilters({ ...filters, section_id: val })}
                disabled={!filters.topic_id || filters.topic_id === 'all'}
              >
                <SelectTrigger>
                  <SelectValue placeholder={filters.topic_id === 'all' ? "Select chapter first" : "All Subchapters"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subchapters</SelectItem>
                  {sortObjectOptions(getFilterSections(), 'section_title').map((section, idx) => {
                    const sectionId = getSubchapterId(section, idx);
                    return (
                      <SelectItem key={String(sectionId)} value={String(sectionId)}>
                        {getSubchapterTitle(section, idx)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {/* Question Type filter - only for platform admin roles */}
            {['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'ExlvaAdminLite'].includes(user?.role) && (
              <div>
                <Label>Question Type</Label>
                <Select
                  value={filters.purpose}
                  onValueChange={(val) => setFilters({ ...filters, purpose: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="adaptive">Adaptive Learning</SelectItem>
                    <SelectItem value="conventional">Conventional Learning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setFilters({ topic_id: 'all', section_id: 'all', search: '', purpose: 'all' })} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Question Count */}
        {!loading && (
          <div className="flex justify-end mb-4">
            <span className="text-xs text-gray-500">
              {questionCount} {questionCount === 1 ? 'question' : 'questions'}
            </span>
          </div>
        )}

        {/* Questions List */}
        {loading ? (
          <div className="text-center py-12">Loading questions...</div>
        ) : questions.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500">No questions found. Create your first question!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {questions.map((question) => (
              <Card key={question.question_id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {getQuestionTypeLabel(question.question_type)}
                      </span>
                      {question.purpose === 'adaptive' && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          ADAPTIVE{question.sequence_number ? ` • Seq ${question.sequence_number}` : ''}
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                        question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {question.difficulty?.toUpperCase()}
                      </span>
                      {question.topic_id && (
                        <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                          📚 {getTopicName(question.topic_id)}
                        </span>
                      )}
                      {question.section_id && getSectionName(question.topic_id, question.section_id) && (
                        <span 
                          className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs max-w-[150px] truncate inline-block"
                          data-tooltip={getSectionName(question.topic_id, question.section_id)}
                        >
                          📑 {truncateText(getSectionName(question.topic_id, question.section_id), 20)}
                        </span>
                      )}
                      {question.available_languages && question.available_languages.length > 1 && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs flex items-center gap-1">
                          <Languages className="w-3 h-3" />
                          {question.available_languages.join(', ')}
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-medium text-gray-900 mb-2">{question.question_text}</p>
                    <p className="text-sm text-gray-600">
                      {question.options?.length || 0} options • {question.points} point(s)
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" data-tooltip="View Question" onClick={() => handleViewQuestion(question)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canModifyQuestion(question) && (
                      <>
                        <Button variant="outline" size="sm" data-tooltip="Edit Question" onClick={() => handleEditQuestion(question)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" data-tooltip="Delete Question" onClick={() => handleDeleteQuestion(question.question_id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Question Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateModal(open); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{isEditMode ? 'Edit Question' : 'Create New Question'}</DialogTitle>
            </DialogHeader>
            
            {/* Language Tabs */}
            <div className="border-b bg-gray-50 -mx-6 px-6">
              <div className="flex items-center overflow-x-auto">
                {activeLangTabs.map((lang) => (
                  <div
                    key={lang}
                    className={`relative flex items-center px-6 py-3 cursor-pointer border-b-2 transition-colors whitespace-nowrap ${
                      currentTab === lang
                        ? 'border-[#0056B3] bg-white text-[#0056B3] font-semibold'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    onClick={() => setCurrentTab(lang)}
                  >
                    <span>{lang}</span>
                    {lang !== 'English' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveLanguage(lang);
                        }}
                        className="ml-2 p-1 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                {activeLangTabs.length < AVAILABLE_LANGUAGES.length && (
                  <button
                    onClick={() => setShowAddLangDialog(true)}
                    className="flex items-center px-6 py-3 text-[#0056B3] hover:bg-blue-50 whitespace-nowrap font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Language
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-1">
              <div className="space-y-6 py-4">
                {/* Question Type - Only show on English tab */}
                {currentTab === 'English' && (
                  <>
                    <div>
                      <Label>Question Type <span className="text-red-500">*</span></Label>
                      <Select value={formData.question_type} onValueChange={(val) => setFormData({ ...formData, question_type: val })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mcq_single">MCQ Single Choice</SelectItem>
                          <SelectItem value="mcq_multiple">MCQ Multiple Choice</SelectItem>
                          <SelectItem value="ranking">Ranking Question</SelectItem>
                          <SelectItem value="arrange">Arrange/Order Question</SelectItem>
                          <SelectItem value="drag_drop">Drag-Drop Matching</SelectItem>
                          <SelectItem value="matching">Matching Pairs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Purpose - only for Super Admin, Exlva Admin, ExlvaAdminLite */}
                    {user && ['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'ExlvaAdminLite'].includes(user.role) && (
                    <div>
                      <Label>Purpose</Label>
                      <Select value={formData.purpose} onValueChange={(val) => setFormData({ ...formData, purpose: val })}>
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="conventional">Conventional Training</SelectItem>
                          <SelectItem value="adaptive">Adaptive Training</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.purpose === 'conventional'
                          ? 'Standard single question format'
                          : 'Each question is mapped to a sequence via dropdown'}
                      </p>
                    </div>
                    )}

                    {/* Training Chapter Selection */}
                    <div>
                      <Label>Training Chapter <span className="text-red-500">*</span></Label>
                      <Select 
                        value={formData.topic_id || undefined} 
                        onValueChange={(val) => setFormData({ ...formData, topic_id: val, section_id: undefined })}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select Training Chapter" />
                        </SelectTrigger>
                        <SelectContent>
                          {sortObjectOptions(getEligibleTopics(), 'title').map((topic) => {
                            const topicId = getChapterId(topic);
                            if (!topicId) return null;
                            return (
                              <SelectItem key={String(topicId)} value={String(topicId)}>
                                {topic.title}
                                {topic.training_type && (
                                  <span className="ml-2 text-xs text-gray-500">({topic.training_type})</span>
                                )}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {user?.role === 'Hospital Admin' && getEligibleTopics().length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ No eligible Online/Hybrid chapters are available.
                        </p>
                      )}
                    </div>

                    {/* Subchapter Selection - Conditional */}
                    {formData.topic_id && (() => {
                      const selectedTopic = findChapterById(formData.topic_id, getEligibleTopics());
                      const sections = selectedTopic?.sections || [];
                      if (sections.length > 0) {
                        return (
                          <div>
                            <Label>Subchapter</Label>
                            <Select 
                              value={formData.section_id || "none"} 
                              onValueChange={(val) => setFormData({ ...formData, section_id: val === 'none' ? undefined : val })}
                            >
                              <SelectTrigger className="mt-2">
                                <SelectValue placeholder="Select Subchapter (Optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No Subchapter</SelectItem>
                                {sortObjectOptions(sections, 'section_title').map((section, idx) => {
                                  const sectionId = getSubchapterId(section, idx);
                                  return (
                                    <SelectItem key={String(sectionId)} value={String(sectionId)}>
                                      {getSubchapterTitle(section, idx)}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}

                {/* === CONVENTIONAL MODE: flat question === */}
                {formData.purpose === 'conventional' && (
                  <>
                    {/* Question Text */}
                    <div>
                      <Label>
                        Question Text ({currentTab}) {currentTab === 'English' && <span className="text-red-500">*</span>}
                      </Label>
                      <textarea
                        className="w-full border rounded-md p-3 min-h-[100px] mt-2"
                        placeholder={`Enter your question in ${currentTab}...`}
                        value={currentContent.question_text || ''}
                        onChange={(e) => updateCurrentLangContent('question_text', e.target.value)}
                      />
                    </div>

                    {/* Options */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <Label>Options ({currentTab}) {currentTab === 'English' && <span className="text-red-500">*</span>}</Label>
                        {currentTab === 'English' && (
                          <Button type="button" onClick={addOption} size="sm" variant="outline">
                            <Plus className="w-3 h-3 mr-1" />
                            Add Option
                          </Button>
                        )}
                      </div>
                      {(currentContent.options || []).map((option, index) => (
                        <div key={option.option_id} className="flex items-center space-x-2 mb-3">
                          <Input
                            placeholder={`Option ${index + 1} in ${currentTab}`}
                            value={option.text || ''}
                            onChange={(e) => updateOption(index, 'text', e.target.value)}
                            className="flex-1"
                          />
                          {['mcq_single', 'mcq_multiple'].includes(formData.question_type) && (
                            <label className="flex items-center space-x-2 cursor-pointer whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={option.is_correct}
                                onChange={(e) => updateOption(index, 'is_correct', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Correct</span>
                            </label>
                          )}
                          {currentTab === 'English' && (currentContent.options || []).length > 2 && (
                            <Button type="button" variant="outline" size="sm" onClick={() => removeOption(index)}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {['ranking', 'arrange'].includes(formData.question_type) && currentTab === 'English' && (
                        <p className="text-xs text-gray-600 mt-2">
                          💡 Note: The order you enter options will be the correct order for ranking/arrange questions.
                        </p>
                      )}
                    </div>

                    {/* Right Answer Explanation */}
                    <div>
                      <Label>Right Answer Explanation ({currentTab})</Label>
                      <textarea
                        className="w-full border rounded-md p-3 mt-2"
                        placeholder={`Explanation shown when learner answers correctly in ${currentTab}...`}
                        value={currentContent.right_instruction || ''}
                        onChange={(e) => updateCurrentLangContent('right_instruction', e.target.value)}
                        rows={2}
                      />
                    </div>

                    {/* Wrong Answer Explanation */}
                    <div>
                      <Label>Wrong Answer Explanation ({currentTab})</Label>
                      <textarea
                        className="w-full border rounded-md p-3 mt-2"
                        placeholder={`Explanation shown when learner answers incorrectly in ${currentTab}...`}
                        value={currentContent.wrong_instruction || ''}
                        onChange={(e) => updateCurrentLangContent('wrong_instruction', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </>
                )}

                {/* === ADAPTIVE MODE: dropdown-based sequence mapping === */}
                {formData.purpose === 'adaptive' && (
                  <>
                    {/* Sequence Dropdown */}
                    {currentTab === 'English' && (
                      <div>
                        <Label>Sequence <span className="text-red-500">*</span></Label>
                        <div className="flex items-center gap-2 mt-2">
                          <Select
                            value={String(selectedSequence)}
                            onValueChange={(val) => setSelectedSequence(parseInt(val))}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {availableSequences.map((num) => (
                                <SelectItem key={num} value={String(num)}>
                                  Sequence {num}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" onClick={addSequence} size="sm" variant="outline">
                            <Plus className="w-3 h-3 mr-1" />
                            Add Sequence
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          This question will be mapped to the selected sequence
                        </p>
                      </div>
                    )}

                    {/* Question Text - same flat form as conventional */}
                    <div>
                      <Label>
                        Question Text ({currentTab}) {currentTab === 'English' && <span className="text-red-500">*</span>}
                      </Label>
                      <textarea
                        className="w-full border rounded-md p-3 min-h-[100px] mt-2"
                        placeholder={`Enter your question in ${currentTab}...`}
                        value={currentContent.question_text || ''}
                        onChange={(e) => updateCurrentLangContent('question_text', e.target.value)}
                      />
                    </div>

                    {/* Options */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <Label>Options ({currentTab}) {currentTab === 'English' && <span className="text-red-500">*</span>}</Label>
                        {currentTab === 'English' && (
                          <Button type="button" onClick={addOption} size="sm" variant="outline">
                            <Plus className="w-3 h-3 mr-1" />
                            Add Option
                          </Button>
                        )}
                      </div>
                      {(currentContent.options || []).map((option, index) => (
                        <div key={option.option_id} className="flex items-center space-x-2 mb-3">
                          <Input
                            placeholder={`Option ${index + 1} in ${currentTab}`}
                            value={option.text || ''}
                            onChange={(e) => updateOption(index, 'text', e.target.value)}
                            className="flex-1"
                          />
                          {['mcq_single', 'mcq_multiple'].includes(formData.question_type) && (
                            <label className="flex items-center space-x-2 cursor-pointer whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={option.is_correct}
                                onChange={(e) => updateOption(index, 'is_correct', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Correct</span>
                            </label>
                          )}
                          {currentTab === 'English' && (currentContent.options || []).length > 2 && (
                            <Button type="button" variant="outline" size="sm" onClick={() => removeOption(index)}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {['ranking', 'arrange'].includes(formData.question_type) && currentTab === 'English' && (
                        <p className="text-xs text-gray-600 mt-2">
                          💡 Note: The order you enter options will be the correct order for ranking/arrange questions.
                        </p>
                      )}
                    </div>

                    {/* Right Answer Explanation */}
                    <div>
                      <Label>Right Answer Explanation ({currentTab})</Label>
                      <textarea
                        className="w-full border rounded-md p-3 mt-2"
                        placeholder={`Explanation shown when learner answers correctly in ${currentTab}...`}
                        value={currentContent.right_instruction || ''}
                        onChange={(e) => updateCurrentLangContent('right_instruction', e.target.value)}
                        rows={2}
                      />
                    </div>

                    {/* Wrong Answer Explanation */}
                    <div>
                      <Label>Wrong Answer Explanation ({currentTab})</Label>
                      <textarea
                        className="w-full border rounded-md p-3 mt-2"
                        placeholder={`Explanation shown when learner answers incorrectly in ${currentTab}...`}
                        value={currentContent.wrong_instruction || ''}
                        onChange={(e) => updateCurrentLangContent('wrong_instruction', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </>
                )}

                {/* Metadata - Only show on English tab */}
                {currentTab === 'English' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Difficulty</Label>
                        <Select value={formData.difficulty} onValueChange={(val) => setFormData({ ...formData, difficulty: val })}>
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="easy">Easy</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="hard">Hard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Points</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.points}
                          onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 1 })}
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t -mx-6 px-6 bg-white">
              <Button variant="outline" onClick={() => { resetForm(); setShowCreateModal(false); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateQuestion} className="bg-blue-600 hover:bg-blue-700">
                {isEditMode ? 'Update Question' : 'Create Question'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Language Dialog */}
        <Dialog open={showAddLangDialog} onOpenChange={setShowAddLangDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Language</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Select a language to add translation for this question.</p>
              <div className="grid grid-cols-2 gap-3">
                {AVAILABLE_LANGUAGES.filter(lang => !activeLangTabs.includes(lang)).map(lang => (
                  <Button
                    key={lang}
                    variant="outline"
                    onClick={() => handleAddLanguage(lang)}
                    className="justify-start"
                  >
                    {lang}
                  </Button>
                ))}
              </div>
              {AVAILABLE_LANGUAGES.filter(lang => !activeLangTabs.includes(lang)).length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">All available languages have been added.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* View Question Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Question Details</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            {selectedQuestion && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-blue-100 text-blue-800">
                    {getQuestionTypeLabel(selectedQuestion.question_type)}
                  </Badge>
                  {selectedQuestion.purpose === 'adaptive' && (
                    <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
                      <Layers className="w-3 h-3" />
                      ADAPTIVE{selectedQuestion.sequence_number ? ` • Sequence ${selectedQuestion.sequence_number}` : ''}
                    </Badge>
                  )}
                  <Badge className={`${
                    selectedQuestion.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                    selectedQuestion.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedQuestion.difficulty?.toUpperCase()}
                  </Badge>
                  {selectedQuestion.topic_id && (
                    <Badge className="bg-indigo-100 text-indigo-800">
                      📚 {getTopicName(selectedQuestion.topic_id)}
                    </Badge>
                  )}
                  {selectedQuestion.section_id && (
                    <Badge className="bg-teal-100 text-teal-800">
                      📑 {getSectionName(selectedQuestion.topic_id, selectedQuestion.section_id)}
                    </Badge>
                  )}
                  {selectedQuestion.available_languages && selectedQuestion.available_languages.length > 1 && (
                    <Badge className="bg-purple-100 text-purple-800">
                      <Languages className="w-3 h-3 mr-1" />
                      {selectedQuestion.available_languages.join(', ')}
                    </Badge>
                  )}
                </div>

                {/* Conventional question view */}
                {selectedQuestion.purpose !== 'adaptive' && (
                  <>
                    <div>
                      <Label className="text-gray-600">Question (English)</Label>
                      <p className="text-lg mt-1">{selectedQuestion.question_text}</p>
                    </div>
                    
                    {selectedQuestion.question_text_translations?.Hindi && (
                      <div>
                        <Label className="text-gray-600">Question (Hindi)</Label>
                        <p className="text-lg mt-1">{selectedQuestion.question_text_translations.Hindi}</p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-600">Options</Label>
                      <div className="space-y-2 mt-2">
                        {selectedQuestion.options?.map((opt, idx) => (
                          <div key={opt.option_id} className={`p-3 rounded border ${opt.is_correct ? 'bg-green-50 border-green-500' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium">English:</span> {opt.text}
                                {opt.text_translations?.Hindi && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    <span className="font-medium">Hindi:</span> {opt.text_translations.Hindi}
                                  </div>
                                )}
                              </div>
                              {opt.is_correct && <span className="text-green-600 font-medium">✓ Correct</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {(selectedQuestion.right_instruction || selectedQuestion.right_instruction_translations?.Hindi) && (
                      <div>
                        <Label className="text-gray-600">Right Answer Explanation</Label>
                        {selectedQuestion.right_instruction && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">English:</span> {selectedQuestion.right_instruction}</p>
                        )}
                        {selectedQuestion.right_instruction_translations?.Hindi && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Hindi:</span> {selectedQuestion.right_instruction_translations.Hindi}</p>
                        )}
                      </div>
                    )}
                    
                    {(selectedQuestion.wrong_instruction || selectedQuestion.wrong_instruction_translations?.Hindi) && (
                      <div>
                        <Label className="text-gray-600">Wrong Answer Explanation</Label>
                        {selectedQuestion.wrong_instruction && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">English:</span> {selectedQuestion.wrong_instruction}</p>
                        )}
                        {selectedQuestion.wrong_instruction_translations?.Hindi && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Hindi:</span> {selectedQuestion.wrong_instruction_translations.Hindi}</p>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Adaptive question view - same as conventional with sequence info */}
                {selectedQuestion.purpose === 'adaptive' && (
                  <>
                    {selectedQuestion.sequence_number && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm text-purple-700 font-medium flex items-center gap-2">
                          <Layers className="w-4 h-4" />
                          Mapped to Sequence {selectedQuestion.sequence_number}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-gray-600">Question (English)</Label>
                      <p className="text-lg mt-1">{selectedQuestion.question_text}</p>
                    </div>
                    
                    {selectedQuestion.question_text_translations?.Hindi && (
                      <div>
                        <Label className="text-gray-600">Question (Hindi)</Label>
                        <p className="text-lg mt-1">{selectedQuestion.question_text_translations.Hindi}</p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-600">Options</Label>
                      <div className="space-y-2 mt-2">
                        {selectedQuestion.options?.map((opt, idx) => (
                          <div key={opt.option_id || idx} className={`p-3 rounded border ${opt.is_correct ? 'bg-green-50 border-green-500' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium">English:</span> {opt.text}
                                {opt.text_translations?.Hindi && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    <span className="font-medium">Hindi:</span> {opt.text_translations.Hindi}
                                  </div>
                                )}
                              </div>
                              {opt.is_correct && <span className="text-green-600 font-medium">✓ Correct</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {(selectedQuestion.right_instruction || selectedQuestion.right_instruction_translations?.Hindi) && (
                      <div>
                        <Label className="text-gray-600">Right Answer Explanation</Label>
                        {selectedQuestion.right_instruction && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">English:</span> {selectedQuestion.right_instruction}</p>
                        )}
                        {selectedQuestion.right_instruction_translations?.Hindi && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Hindi:</span> {selectedQuestion.right_instruction_translations.Hindi}</p>
                        )}
                      </div>
                    )}
                    
                    {(selectedQuestion.wrong_instruction || selectedQuestion.wrong_instruction_translations?.Hindi) && (
                      <div>
                        <Label className="text-gray-600">Wrong Answer Explanation</Label>
                        {selectedQuestion.wrong_instruction && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">English:</span> {selectedQuestion.wrong_instruction}</p>
                        )}
                        {selectedQuestion.wrong_instruction_translations?.Hindi && (
                          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Hindi:</span> {selectedQuestion.wrong_instruction_translations.Hindi}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default QuestionBankPage;
